import React, { Component, createRef } from 'react';
import noop from 'lodash/noop';
import ShortcutIcon from '@atlaskit/icon/core/migration/link-external--shortcut';
// eslint-disable-next-line jira/wrm/no-load-bridge
import { waitForHeritage, loadBridgeStrict } from '@atlassian/jira-common-bridge/src';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import ErrorBoundary from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { NavigationItem } from '@atlassian/jira-navigation-types/src/types.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import {
	toBaseUrl,
	toProjectKey,
	type RefObject,
} from '@atlassian/jira-shared-types/src/general.tsx';
import { lazy, LazyWait } from '@atlassian/react-loosely-lazy';
import type AddLinkModal from './add-link-modal/view/index.tsx';
import { REPO_TYPE } from './common/const.tsx';
import type { ItemType, ConsumerRenderProps, PagesLink } from './common/model/index.tsx';
import type NewAddItemModal from './main/view/add-item-v2/index.tsx';
import type AddItemModalType from './main/view/add-item/index.tsx';
import type EditShortcutModal from './main/view/edit-shortcut-modal/index.tsx';
import type ManagePagesLinkModal from './manage-pages-link-modal/view/index.tsx';
import { getIdFromLinkId, getItemFromLink } from './utils/index.tsx';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
export const LazyAddLinkModal = lazy<typeof AddLinkModal>(
	() => import(/* webpackChunkName: "async-navigation-shortcuts" */ './add-link-modal'),
);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
export const LazyAddItemModal = lazy<typeof AddItemModalType>(
	() =>
		// eslint-disable-next-line jira/import/webpack-magic-comment-entries
		import(/* webpackChunkName: "async-navigation-shortcuts" */ './main/view/add-item'),
);
// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
export const LazyNewAddItemModal = lazy<typeof NewAddItemModal>(
	() =>
		// eslint-disable-next-line jira/import/webpack-magic-comment-entries
		import(/* webpackChunkName: "async-navigation-shortcuts" */ './main/view/add-item-v2'),
);
// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
export const LazyEditShortcutModal = lazy<typeof EditShortcutModal>(
	() =>
		// eslint-disable-next-line jira/import/webpack-magic-comment-entries
		import(/* webpackChunkName: "async-navigation-shortcuts" */ './main/view/edit-shortcut-modal'),
);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
export const LazyManagePagesLinkModal = lazy<typeof ManagePagesLinkModal>(
	() =>
		// eslint-disable-next-line jira/import/webpack-magic-comment-entries
		import(/* webpackChunkName: "async-navigation-shortcuts" */ './manage-pages-link-modal'),
);

const MODAL_ANIMATION_DELAY = 500;

export type Props = {
	isSpa: boolean; // for legacy page case,
	isAdmin: boolean;
	canAddLinks: boolean;
	canAddRepositories: boolean;
	baseUrl: string;
	projectKey: string;
	projectId: number;
	pagesLink?: PagesLink;
	showPagesSelector: boolean;
	isModalOpenViaAddView?: boolean;
	setIsModalOpenViaAddView?: (arg1: boolean) => void;
	children: ConsumerRenderProps;
	items: NavigationItem[];
	methods: {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		addItem: (arg1: NavigationItem) => any;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		deleteItem: (arg1: string) => any;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		editItem: (arg1: NavigationItem) => any;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		editItems: (arg1: NavigationItem[]) => any;
	};
	addItemButtonRef?: RefObject;
};

type State = {
	canAddRepositories: boolean;
	addItem:
		| {
				type: ItemType | undefined;
		  }
		| undefined;
	editOrDeleteItem: string | undefined;
	isPagesModalOpen: boolean;
	editShortcutButtonRef: React.RefObject<HTMLButtonElement> | undefined;
};

const renderShortcutIcon = () => <ShortcutIcon spacing="spacious" label="" />;

const findRepoShortcuts = (items: Array<NavigationItem>) =>
	items.filter((i) => i.shortcutType === REPO_TYPE);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const toItem = (link: any) => {
	// We cannot create shortcuts with legacyId as it would exclude them from the list of navigation items -
	// they don't exist in the sidebar config we've fetched before
	const { legacyId, ...withoutLegacyId } = getItemFromLink(link);
	return {
		...withoutLegacyId,
		onEditClick: () => setTimeout(() => link.onEditClick()),
		onSyncClick: link.onSyncClick,
	};
};

// eslint-disable-next-line jira/react/no-class-components
export default class Shortcuts extends Component<Props, State> {
	static defaultProps = {
		isSpa: false,
		isAdmin: false,
		canAddLinks: true,
		canAddRepositories: false,
		showPagesSelector: true,
		items: [],
	};

	constructor(props: Props) {
		super(props);
		this.state = {
			canAddRepositories: props.canAddRepositories,
			addItem: undefined,
			editOrDeleteItem: undefined,
			isPagesModalOpen: false,
			editShortcutButtonRef: undefined,
		};

		if (props.canAddRepositories) {
			this.initLegacyBridgeAPI();
		}
	}

	componentDidMount() {
		if (fg('jsw_simplify_navigation_m2_gate')) {
			this.showModalIfAddShortcutClickedFromAddView();
		}
	}

	componentDidUpdate(prevProps: Props) {
		if (fg('jsw_simplify_navigation_m2_gate')) {
			if (this.props.isModalOpenViaAddView !== prevProps.isModalOpenViaAddView) {
				this.showModalIfAddShortcutClickedFromAddView();
			}
		}
	}

	showModalIfAddShortcutClickedFromAddView() {
		if (this.props.isModalOpenViaAddView === true) {
			this.onAddItem();
			this.props.setIsModalOpenViaAddView?.(false);
		}
	}

	addItemButtonRef = createRef<HTMLElement>();

	onAddItem = () => {
		if (this.props.canAddLinks && !this.state.canAddRepositories) {
			this.onAddLink();
		} else if (this.state.canAddRepositories && !this.props.canAddLinks) {
			this.onAddRepository();
		} else {
			this.setState({ addItem: { type: undefined } });
		}
	};

	onAddLink = () => {
		// if a modal is already open, close it and open the add link modal
		// with a delay to ensure the modal is closed before opening the new one
		if (this.state.addItem) {
			this.setState({ addItem: undefined });

			// wait for the modal to close before opening the next modal
			setTimeout(() => {
				this.setState({ addItem: { type: 'link' } });
			}, MODAL_ANIMATION_DELAY);
		} else {
			this.setState({ addItem: { type: 'link' } });
		}
	};

	onAddRepository = () => {
		this.setState(() => ({ addItem: undefined }));

		setTimeout(() => {
			if (typeof this.repositoryApi.add === 'function') {
				this.repositoryApi.add();
			} else {
				log.safeErrorWithoutCustomerData(
					'navigation-next.shortcuts.repository',
					"Cannot open add repository shortcut modal - the API wan't set up",
				);
			}
		}, MODAL_ANIMATION_DELAY);
	};

	onAddItemCompleted = ({ item }: { item: NavigationItem | undefined }) => {
		if (item) {
			this.props.methods.addItem({
				...item,
				target: '_blank',
				canEdit: true,
				before: renderShortcutIcon,
			});
		}

		this.setState(
			() => {
				return {
					addItem: undefined,
				};
			},
			() => {
				if (this.addItemButtonRef?.current) {
					this.addItemButtonRef.current.focus();
				}
			},
		);
	};

	onEditOrDeleteItem = (id: string, ref?: React.RefObject<HTMLButtonElement>) => {
		if (ref) {
			this.setState({
				editOrDeleteItem: id,
				editShortcutButtonRef: ref,
			});
		} else {
			this.setState({
				editOrDeleteItem: id,
			});
		}
	};

	openManagePagesLinkModal = () => {
		this.setState({ isPagesModalOpen: true });
	};

	closeManagePagesLinkModal = () => {
		this.setState({ isPagesModalOpen: false });
	};

	onEditOrDeleteItemCompleted = (args: { item: NavigationItem | undefined; deleted: boolean }) => {
		const { item, deleted } = args;
		if (item) {
			deleted ? this.props.methods.deleteItem(item.id) : this.props.methods.editItem(item);
		}
		this.setState(() => {
			return { editOrDeleteItem: undefined, editShortcutButtonRef: undefined };
		});
	};

	setPagesLink =
		({ setPagesLink }: PagesLink) =>
		(isEnabled: boolean) => {
			const { baseUrl, projectKey } = this.props;
			return setPagesLink({
				baseUrl: toBaseUrl(baseUrl),
				projectKey: toProjectKey(projectKey),
				enable: isEnabled,
			});
		};

	initLegacyBridgeAPI() {
		// eslint-disable-next-line jira/wrm/no-load-bridge
		waitForHeritage()
			?.then(() =>
				// eslint-disable-next-line jira/wrm/no-load-bridge
				loadBridgeStrict({
					name: 'repository-shortcuts/navigation-next',
					wrmKeys: [
						'wrc!com.atlassian.jira.plugins.jira-development-integration-plugin:repository-shortcuts-navigation-next',
					],
				}),
			) // eslint-disable-next-line @typescript-eslint/no-explicit-any
			?.then((init: any) => {
				const { methods, items, projectKey, projectId, isAdmin } = this.props;
				this.repositoryApi = init({
					shortcuts: findRepoShortcuts(items),
					// @ts-expect-error - TS7006 - Parameter 'item' implicitly has an 'any' type.
					onAdd: (item) => methods.addItem(toItem(item)),
					// @ts-expect-error - TS7006 - Parameter 'item' implicitly has an 'any' type.
					onEdit: (item) => methods.editItem(toItem(item)),
					// @ts-expect-error - TS7006 - Parameter 'id' implicitly has an 'any' type.
					onDelete: (id) => methods.deleteItem(getIdFromLinkId(id)),
					// @ts-expect-error - TS7006 - Parameter 'itemBulk' implicitly has an 'any' type.
					onBulkEdit: (itemBulk) =>
						// @ts-expect-error - TS7006 - Parameter 'item' implicitly has an 'any' type.
						methods.editItems(itemBulk.map((item) => toItem(item))),
					projectKey,
					projectId,
					isAdmin,
				});
			}) // eslint-disable-next-line @typescript-eslint/no-explicit-any
			?.catch((e: any) => {
				this.setState({
					canAddRepositories: false,
				});

				log.safeErrorWithoutCustomerData(
					'navigation-next.shortcuts.repository',
					'There was a problem loading or integrating with repository shortcuts',
					e,
				);
			});
	}

	repositoryApi: {
		add?: () => void;
	} = {};

	render() {
		const { children, baseUrl, pagesLink, projectKey, items, showPagesSelector } = this.props;
		const { editOrDeleteItem, addItem, isPagesModalOpen } = this.state;
		return (
			<>
				{children({
					data: { shortcuts: items },
					openAddModal: this.onAddItem,
					openEditModal: this.onEditOrDeleteItem,
					openManagePagesLinkModal: pagesLink ? this.openManagePagesLinkModal : noop,
					addItemButtonRef: this.addItemButtonRef,
				})}
				<ErrorBoundary id="shortcuts-modal" packageName="jiraNavigationShortcuts">
					{pagesLink && (
						<LazyWait
							until={
								fg('nav_prevent_eager_manage_pages_modal')
									? isPagesModalOpen === true
									: isPagesModalOpen != null
							}
						>
							<Placeholder name="manage-pages-link-modal" fallback={<></>}>
								<LazyManagePagesLinkModal
									config={{
										isPagesLinkEnabled: pagesLink.isEnabled,
										setPagesLink: this.setPagesLink(pagesLink),
									}}
									isOpen={isPagesModalOpen}
									onClose={this.closeManagePagesLinkModal}
								/>
							</Placeholder>
						</LazyWait>
					)}
					<LazyWait until={addItem != null}>
						<Placeholder name="add-item-modal" fallback={<></>}>
							{pagesLink ? (
								// add shortcut modal
								<LazyNewAddItemModal
									addItem={addItem}
									baseUrl={baseUrl}
									pages={{
										isEnabled: pagesLink.isEnabled,
										setPagesLink: this.setPagesLink(pagesLink),
									}}
									projectKey={projectKey}
									onAddItemCompleted={this.onAddItemCompleted}
									onAddLink={this.onAddLink}
									onAddRepo={this.onAddRepository}
									showPagesSelector={showPagesSelector}
									addItemButtonRef={this.addItemButtonRef}
								/>
							) : (
								<LazyAddItemModal
									addItem={addItem}
									baseUrl={baseUrl}
									projectKey={projectKey}
									onAddItemCompleted={this.onAddItemCompleted}
									onAddLink={this.onAddLink}
									onAddRepo={this.onAddRepository}
								/>
							)}
						</Placeholder>
					</LazyWait>
					<LazyWait until={editOrDeleteItem != null}>
						<Placeholder name="edit-shortcut-modal" fallback={<></>}>
							{/* edit shortcut shortcut */}
							<LazyEditShortcutModal
								baseUrl={baseUrl}
								projectKey={projectKey}
								editOrDeleteItem={editOrDeleteItem}
								items={items}
								editOrDeleteItemCompleted={this.onEditOrDeleteItemCompleted}
								editButtonRef={this.state.editShortcutButtonRef}
							/>
						</Placeholder>
					</LazyWait>
				</ErrorBoundary>
			</>
		);
	}
}
